import { TOAST_VARIANT } from "@root/constants";
import { validatePdfIsTextBased } from "./pdf-text-validator";
import { useNotification } from "@root/hooks/useNotification";
import { useTranslation } from "react-i18next";

/**
 * Validates file type based on context (drawing analysis vs legal chatbot)
 */
export const validateFileType = (
  file: File,
  isDrawingAnalysis: boolean,
  isLegalChatbot: boolean
): boolean => {
  const ext = file?.name?.split(".").pop()?.toLowerCase();

  if (isDrawingAnalysis && ext !== "pdf") {
    return false;
  }

  if (!isDrawingAnalysis && ext !== "pdf" && ext !== "docx") {
    return false;
  }

  return true;
};

/**
 * Validates PDF is text-based (non-scanned) for legal chatbot
 */
export const validatePdfTextBased = async (
  file: File,
  isLegalChatbot: boolean
): Promise<boolean> => {
  if (!isLegalChatbot) return true;

  const ext = file?.name?.split(".").pop()?.toLowerCase();
  if (ext !== "pdf") return true;

  try {
    return await validatePdfIsTextBased(file);
  } catch (error) {
    console.error("Error validating PDF:", error);
    return false;
  }
};

/**
 * Hook-based file validation with notifications
 */
export const useFileValidation = () => {
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");

  const validateFile = async (
    file: File,
    isDrawingAnalysis: boolean,
    isLegalChatbot: boolean
  ): Promise<boolean> => {
    // Validate file type
    if (!validateFileType(file, isDrawingAnalysis, isLegalChatbot)) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("upload_document.invalid_file_type"),
      });
      return false;
    }

    // Validate PDF is text-based for legal chatbot
    if (isLegalChatbot) {
      try {
        const isTextBased = await validatePdfTextBased(file, isLegalChatbot);
        if (!isTextBased) {
          showNotification({
            type: TOAST_VARIANT.ERROR,
            message: t("systemChat.noReadableData"),
          });
          return false;
        }
      } catch (error) {
        console.error("Error validating PDF:", error);
        showNotification({
          type: TOAST_VARIANT.ERROR,
          message:
            t("systemChat.pdfValidationError") ||
            t("systemChat.errorMessage"),
        });
        return false;
      }
    }

    return true;
  };

  return { validateFile };
};
